import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BillingDocument } from '@/hooks/useBillingDocuments';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export const generateBillingDocumentPDF = (document: BillingDocument) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Colors (matching design system) - as tuples
  const primaryBlue: [number, number, number] = [55, 105, 158]; // #37699E
  const accentTeal: [number, number, number] = [8, 128, 174]; // #0880AE
  const yellow: [number, number, number] = [255, 191, 7]; // #FFBF07
  const textDark: [number, number, number] = [45, 55, 72]; // #2D3748
  const textGray: [number, number, number] = [90, 107, 122]; // #5A6B7A
  
  const isInvoice = document.document_type === 'invoice';
  const docTitle = isInvoice ? 'FACTURE' : 'BON DE COMMANDE';
  
  // Header - Logo and Title
  doc.setFillColor(...yellow);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setFontSize(28);
  doc.setTextColor(...primaryBlue);
  doc.setFont('helvetica', 'bold');
  doc.text('VELOX', 20, 20);
  
  doc.setFontSize(20);
  doc.setTextColor(...textDark);
  doc.text(docTitle, 20, 32);
  
  // Document number and date (top right)
  doc.setFontSize(10);
  doc.setTextColor(...textGray);
  doc.text(`N° ${document.document_number}`, pageWidth - 20, 20, { align: 'right' });
  doc.text(
    `Émis le ${format(new Date(document.issued_at), 'dd/MM/yyyy', { locale: fr })}`,
    pageWidth - 20,
    27,
    { align: 'right' }
  );
  
  let yPos = 55;
  
  // Custom title if exists (only for invoices)
  if (isInvoice && document.title) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryBlue);
    doc.text(document.title, 15, yPos);
    yPos += 10;
  }
  
  // Company details - Two columns
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...accentTeal);
  doc.text('EXPÉDITEUR', 20, yPos);
  doc.text('TRANSPORTEUR', 110, yPos);
  
  yPos += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...textDark);
  
  // Shipper details
  const shipperLines = [
    document.shipper_companies?.name || '',
    document.shipper_companies?.email || '',
    document.shipper_companies?.phone ? `Tél: ${document.shipper_companies.phone}` : '',
    document.shipper_companies?.siret ? `ID fiscal: ${document.shipper_companies.siret}` : '',
    document.shipper_companies?.address || '',
  ].filter(Boolean);
  
  shipperLines.forEach((line, index) => {
    doc.text(line, 20, yPos + (index * 5));
  });
  
  // Transporter details
  const transporterLines = [
    document.transporter_companies?.name || 'N/A',
    document.transporter_companies?.email || '',
    document.transporter_companies?.phone ? `Tél: ${document.transporter_companies.phone}` : '',
    document.transporter_companies?.siret ? `ID fiscal: ${document.transporter_companies.siret}` : '',
    document.transporter_companies?.address || '',
  ].filter(Boolean);
  
  transporterLines.forEach((line, index) => {
    doc.text(line, 110, yPos + (index * 5));
  });
  
  yPos += Math.max(shipperLines.length, transporterLines.length) * 5 + 10;
  
  // Order details section
  doc.setFillColor(235, 244, 248); // #EBF4F8
  doc.rect(15, yPos, pageWidth - 30, 25, 'F');
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...accentTeal);
  doc.text('DÉTAILS DE LA COMMANDE', 20, yPos + 7);
  
  yPos += 13;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...textDark);
  
  const orderDetails = [
    `Commande: ${document.orders?.order_number || 'N/A'}`,
    `Marchandise: ${document.orders?.cargo_title || 'N/A'}`,
    `Trajet: ${document.orders?.pickup_city || 'N/A'} → ${document.orders?.delivery_city || 'N/A'}`,
  ];
  
  orderDetails.forEach((detail, index) => {
    doc.text(detail, 20, yPos + (index * 5));
  });
  
  yPos += 25;
  
  // Financial details table
  autoTable(doc, {
    startY: yPos,
    head: [['Description', 'Montant HT']],
    body: [
      ['Prestation de transport', `${document.amount.toFixed(2)} €`],
    ],
    theme: 'grid',
    headStyles: {
      fillColor: primaryBlue,
      textColor: [255, 255, 255],
      fontSize: 11,
      fontStyle: 'bold',
    },
    bodyStyles: {
      textColor: textDark,
      fontSize: 11,
      fontStyle: 'bold',
    },
    columnStyles: {
      0: { cellWidth: 130 },
      1: { halign: 'right', cellWidth: 50 },
    },
  });
  
  yPos = (doc as any).lastAutoTable.finalY + 15;
  
  // Payment terms and dates
  if (document.due_date) {
    doc.setFontSize(10);
    doc.setTextColor(...textGray);
    doc.text(
      `Date d'échéance: ${format(new Date(document.due_date), 'dd/MM/yyyy', { locale: fr })}`,
      20,
      yPos
    );
    yPos += 7;
  }
  
  if (document.paid_at) {
    doc.setTextColor(20, 163, 139); // #14A38B (green)
    doc.setFont('helvetica', 'bold');
    doc.text(
      `✓ Payé le ${format(new Date(document.paid_at), 'dd/MM/yyyy', { locale: fr })}`,
      20,
      yPos
    );
    yPos += 7;
  }
  
  // Notes
  if (document.notes) {
    yPos += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textGray);
    doc.text('Notes:', 20, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    const splitNotes = doc.splitTextToSize(document.notes, pageWidth - 40);
    doc.text(splitNotes, 20, yPos);
    yPos += splitNotes.length * 5;
  }
  
  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 20;
  doc.setFontSize(8);
  doc.setTextColor(...textGray);
  doc.text(
    'VELOX - Plateforme de gestion logistique',
    pageWidth / 2,
    footerY,
    { align: 'center' }
  );
  doc.text(
    `Document généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })}`,
    pageWidth / 2,
    footerY + 5,
    { align: 'center' }
  );
  
  // Save the PDF
  const filename = `${document.document_number}_${isInvoice ? 'facture' : 'bon_de_commande'}.pdf`;
  doc.save(filename);
};
